import { cn } from '@/lib/utils';
import { LayoutDashboard } from 'lucide-react';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { Kbd } from '@/components/Kbd';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useHotkey } from '@/hooks/useHotkey';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    navigate?: (to: string) => void;
    showText?: boolean;
    tooltip?: boolean;
    tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
    hotkey?: string;
}

const DMArtifactsBtn = ({
    navigate,
    showText = true,
    tooltip = true,
    tooltipSide = 'right',
    hotkey,
    ...buttonProps
}: Props) => {
    const location = useLocation();
    const isActive = location.pathname.startsWith('/artifacts');
    const { isMobile, setOpenMobile } = useSidebar();

    const handleClick = () => {
        navigate?.('/artifacts');
        // Mirrors TimeGroupedThreadList's thread rows — picking a
        // destination from the mobile sidebar should close the off-canvas
        // drawer, not leave it open over the page it just navigated to.
        if (isMobile) setOpenMobile(false);
    };

    useHotkey(hotkey, handleClick);

    const button = (
        <Button
            variant="bare"
            id="artifacts-button"
            className={cn(
                'rounded-lg font-normal text-foreground transition-all duration-200',
                isActive && 'text-foreground font-medium',
                showText
                    ? 'group/nav-item justify-start gap-3 px-3 w-full h-10 text-base leading-6'
                    : 'justify-center w-full h-10 p-2'
            )}
            active={isActive}
            onClick={handleClick}
            {...buttonProps}
        >
            <LayoutDashboard strokeWidth={1.5} className="size-5! shrink-0" />
            {showText && (
                <>
                    <span className="flex-1 min-w-0 text-left truncate">
                        Artifacts
                    </span>
                    {hotkey && (
                        <Kbd className="hidden group-hover/nav-item:inline-flex shrink-0">
                            {hotkey}
                        </Kbd>
                    )}
                </>
            )}
        </Button>
    );

    const buttonContent = !showText ? (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side={tooltipSide}>
                    Artifacts
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    ) : (
        button
    );

    return <div className="flex justify-center">{buttonContent}</div>;
};

export default DMArtifactsBtn;
